import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {GRAY, GRAY_LIGHT, WHITE, hexToRgba} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';

import type {ChatLayoutProps} from '../ChatLayoutRegistry';

/**
 * Workspace layout for Analysis chat.
 *
 * Shows: [Plan pane] | [Chat pane]
 *
 * - Plan pane: hidden until plan artifact appears from application state
 * - Chat pane: standard chat content
 *
 */
export function AnalysisWorkspaceLayout({chatContent, artifacts, isEditable}: ChatLayoutProps): JSX.Element {
  const {planArtifact} = artifacts;
  const [isDragging, setIsDragging] = useState(false);

  useOnMount(() => {
    SidepanelActions.expand();
  });

  // Chat-only: no panel chrome needed
  if (!planArtifact) {
    return (
      <div aria-label="Analysis workspace" className={css(styles.container)}>
        {chatContent}
      </div>
    );
  }

  return (
    <div className={css(styles.container)} aria-label="Analysis workspace">
      <PanelGroup direction="horizontal" style={{width: '100%', height: '100%'}}>
        {/* Plan panel */}
        <Panel defaultSize={20} minSize={15} maxSize={35} className={css(styles.panel)}>
          <div role="region" aria-label="Plan panel" className={css(styles.panelWrapper)}>
            <div className={css(styles.paneHeader)}>Plan</div>
            <div className={css(styles.scrollableContent)}>
              <planArtifact.Component data={planArtifact.data} editable={isEditable} />
            </div>
          </div>
        </Panel>

        <PanelResizeHandle
          className={css(styles.resizer, isDragging && styles.resizerActive)}
          onDragging={setIsDragging}
        >
          <div aria-hidden="true" className={css(styles.resizerWrapper)}>
            <div className={css(styles.resizerHandle)} />
          </div>
        </PanelResizeHandle>

        {/* Chat panel */}
        <Panel defaultSize={80} minSize={20} maxSize={85} className={css(styles.panel)}>
          <div role="region" aria-label="Chat panel" className={css(styles.panelWrapper)}>
            <div className={css(styles.scrollableContent)}>{chatContent}</div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  panel: {
    height: '100%',
  },
  panelWrapper: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: WHITE,
  },
  paneHeader: {
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 600,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flexShrink: 0,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  resizer: {
    width: '12px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.3)})`,
    },
    boxShadow: `2px 0 8px 0 ${GRAY}, inset -1px 0 0 0 ${GRAY_LIGHT}`,
  },
  resizerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizerActive: {
    backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.5)})`,
  },
  resizerHandle: {
    width: '2px',
    height: '40px',
    backgroundColor: GRAY_LIGHT,
    borderRadius: '2px',
    transition: 'all 0.15s ease',
  },
});
